import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { CheckCircle2 } from 'lucide-react'


export default function VerifyEmail() {
  const [email, setEmail] = useState('')
  const [resendLoading, setResendLoading] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { toast } = useToast()

  useEffect(() => {
    const emailFromState = location.state?.email
    if (emailFromState) setEmail(emailFromState)
  }, [location])

  const handleResendEmail = async () => {
    if (!email) {
      toast({
        title: 'E-mail necessário',
        description: 'Por favor, informe seu e-mail para reenviar a confirmação.',
        variant: 'destructive',
      })
      return
    }

    setResendLoading(true)
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
      })

      if (error) {
        const isTooMany =
          error.message.includes('429') || error.message.includes('Too Many Requests')
        toast({
          title: isTooMany ? 'Muitas tentativas' : 'Erro ao reenviar',
          description: isTooMany
            ? 'Você fez muitas solicitações. Aguarde alguns minutos antes de tentar novamente.'
            : error.message,
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'E-mail reenviado!',
          description: 'Verifique sua caixa de entrada e spam.',
        })
      }
    } catch {
      toast({
        title: 'Erro inesperado',
        description: 'Tente novamente mais tarde.',
        variant: 'destructive',
      })
    } finally {
      setResendLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#2a20ad] to-[#655bf1]">
      <div className="flex flex-col items-center gap-8 w-full max-w-[447px] px-4">
        {/* Logo */}
        <img
          src="/logos/logo-fluity-white.svg"
          alt="Fluity"
          className="h-10 w-auto object-contain animate-in fade-in slide-in-from-top-4 duration-700"
        />

        {/* Card */}
        <div className="bg-white rounded-[24px] px-6 py-8 w-full flex flex-col gap-8 items-center animate-in fade-in slide-in-from-bottom-6 duration-700 delay-150">
          {/* Header */}
          <div className="flex flex-col gap-1.5 items-center text-center w-full max-w-[309px]">
            <h1 className="text-[#0f172a] text-2xl font-semibold leading-8 tracking-[-0.53px] w-full"
              style={{ fontFamily: "'Inter', sans-serif" }}>
              Confirme o seu e-mail
            </h1>
            <p className="text-[#64748b] text-sm leading-5 w-full"
              style={{ fontFamily: "'Inter', sans-serif" }}>
              Enviamos um e-mail de confirmação para você
            </p>
          </div>

          {/* Info box */}
          <div className="bg-[#eff6ff] border border-[#bfdbfe] rounded-[20px] p-4 w-full flex gap-3 min-h-[118px]">
            <CheckCircle2 className="text-blue-500 h-5 w-5 mt-0.5 shrink-0" />
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium text-black leading-5"
                style={{ fontFamily: "'Inter', sans-serif" }}>
                Verifique sua caixa de entrada
              </p>
              <p className="text-sm text-[#577eaf] leading-5"
                style={{ fontFamily: "'Inter', sans-serif" }}>
                Clique no link de confirmação que enviamos para você. Não esqueça de verificar a pasta de spam.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-4 w-full">
            <button
              onClick={handleResendEmail}
              disabled={resendLoading}
              className="w-full h-12 bg-[#2563EB] hover:bg-[#1d4ed8] disabled:opacity-50 disabled:cursor-not-allowed rounded-[12px] text-white text-sm font-medium tracking-[-0.15px] transition-colors shadow-[0px_0px_20px_0px_rgba(37,99,235,0.35)]"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {resendLoading ? 'Reenviando...' : 'Reenviar e-mail'}
            </button>

            <button
              onClick={() => navigate('/login')}
              className="w-full h-12 rounded-[12px] text-black text-sm font-medium tracking-[-0.15px] underline hover:opacity-70 transition-opacity bg-transparent border-none"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Já confirmou? Fazer login
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
